import { z } from 'zod';
import type { RelationshipType } from './Relationship';
import { relationshipSchema, RelationshipTypes } from './Relationship';

const crmParentValues = [
  'accounts',
  'contacts',
  'documents',
  'leads',
  'opportunities',
] as const;

const crmDetailValues = [
  'account',
  'contact',
  'document',
  'lead',
  'opportunity',
] as const;

const crmDetailTabValues = [
  'overview',
  'relationships',
  'deals',
  'activity',
  'files',
] as const;

export const crmParentName = z.enum(crmParentValues);
export const crmDetailName = z.enum(crmDetailValues);
export const crmDetailTabName = z.enum(crmDetailTabValues);

export type CrmParentName = z.infer<typeof crmParentName>;
export type CrmDetailName = z.infer<typeof crmDetailName>;
export type CrmDetailTabName = z.infer<typeof crmDetailTabName>;

const capitalize = <T extends string>(input: T): Capitalize<T> => {
  if (!input) return undefined;
  const first = input.charAt(0);
  const rest = input.slice(1);
  return `${first.toUpperCase()}${rest}` as Capitalize<T>;
};

const singularToPluralTransformer = crmDetailName.transform(name => {
  switch (name) {
    case 'account':
      return 'accounts';
    case 'contact':
      return 'contacts';
    case 'document':
      return 'documents';
    case 'lead':
      return 'leads';
    case 'opportunity':
      return 'opportunities';
    default:
      return undefined;
  }
});

export const pluralToSingularTransformer = crmParentName.transform(name => {
  switch (name) {
    case 'accounts':
      return 'account';
    case 'contacts':
      return 'contact';
    case 'documents':
      return 'document';
    case 'leads':
      return 'lead';
    case 'opportunities':
      return 'opportunity';
    default:
      return undefined;
  }
});

export const isValidParentName = (name: unknown): name is CrmParentName => {
  try {
    crmParentName.parse(name);
    return true;
  } catch {
    return false;
  }
};

export const isValidDetailName = (name: unknown): name is CrmDetailName => {
  try {
    crmDetailName.parse(name);
    return true;
  } catch {
    return false;
  }
};

export const isValidDetailTabName = (name: unknown): name is CrmDetailTabName => {
  try {
    crmDetailTabName.parse(name);
    return true;
  } catch {
    return false;
  }
};

export const crmRouteNameToRelationshipType = z.union([crmParentName, crmDetailName])
  .transform(name => {
    if (isValidParentName(name)) {
      return capitalize(pluralToSingularTransformer.parse(name)) as RelationshipType;
    }

    if (isValidDetailName(name)) {
      return capitalize(name) as RelationshipType;
    }

    return undefined;
  });

export const relationshipTypeToCrmParent = RelationshipTypes.transform(type => {
  const lower = type.toLowerCase();
  return singularToPluralTransformer.parse(lower) as CrmParentName;
});

export const relationshipTypeToCrmDetail = RelationshipTypes.transform(type => type.toLowerCase() as CrmDetailName);

export const relationshipToCrmParent = relationshipSchema.transform(relationship => relationshipTypeToCrmParent.parse(relationship.type));

export const relationshipToCrmDetail = relationshipSchema.transform(relationship => relationshipTypeToCrmDetail.parse(relationship.type));

export const relationshipToCrmDetailRoutes = relationshipSchema.transform(relationship => [
  relationshipTypeToCrmParent.parse(relationship.type),
  relationshipTypeToCrmDetail.parse(relationship.type),
] as [parent: CrmParentName, detail: CrmDetailName]);
